/**
 * @fileoverview Client-side experiment bootstrap (replaces the server-streamed shell).
 * @description Fetches the visitor's assignments post-hydration from
 * `GET /api/experiments/assignments`, feeds the client store + exposure
 * context, and mounts the exposure beacon and Vercel FlagValues correlation.
 * Renders no visible DOM and adds NO server Suspense hole — the homepage
 * stays fully static (see `assignments-client-store.ts` for why that is a
 * hard requirement).
 *
 * ROUTE-GATED: the assignments fetch, exposure beacon, and FlagValues only
 * engage when at least one RUNNING experiment is eligible for the current
 * route. On a route (or during a period) with no eligible running experiment,
 * this fires no network request and mounts no beacon — the feature costs pages
 * it doesn't touch nothing. A `global` experiment (e.g. the footer) is eligible
 * everywhere, so it engages site-wide by design; a `path`-scoped experiment
 * engages only on its route. Once the store has been populated on an eligible
 * route, the provider + FlagValues stay mounted across SPA navigation so
 * route-change pageviews keep their variant annotation (#233 `r3519039469`).
 */

"use client"

import { usePathname } from "next/navigation"
import { useEffect, useSyncExternalStore } from "react"
import { FlagValues } from "flags/react"

import { ExperimentExposureBeacon } from "~/components/experiments/ExperimentExposureBeacon"
import {
  ensureAssignmentsFetched,
  getFetchedAssignments,
  getFetchedFlagValues,
  subscribeAssignments,
} from "~/lib/experiments/assignments-client-store"
import { ExperimentExposureProvider } from "~/lib/experiments/exposure-context"
import { EXPERIMENT_REGISTRY } from "~/lib/experiments/registry"
import { isSurfaceMatch } from "~/lib/experiments/surfaces"

/** True when ≥1 `running` experiment's surface matches `pathname`. */
function hasRunningExperimentForPath(pathname: string): boolean {
  return EXPERIMENT_REGISTRY.some(
    (e) => e.status === "running" && isSurfaceMatch(e.surface, pathname),
  )
}

/**
 * Mounts experiment analytics plumbing for a page, entirely client-side.
 *
 * @returns {React.ReactNode} Provider + beacons (no visible DOM), or null when
 *   no running experiment applies to this route and nothing is in flight.
 */
export function ExperimentClientBootstrap(): React.ReactNode {
  const pathname = usePathname()
  const eligible = hasRunningExperimentForPath(pathname)
  const assignments = useSyncExternalStore(
    subscribeAssignments,
    getFetchedAssignments,
    getFetchedAssignments,
  )
  const flagValues = useSyncExternalStore(
    subscribeAssignments,
    getFetchedFlagValues,
    () => undefined,
  )

  useEffect(() => {
    // Only reach out when a running experiment actually applies to this route.
    if (eligible) {
      void ensureAssignmentsFetched()
    }
  }, [eligible])

  // Nothing to do: no running experiment for this route AND the store carries no
  // prior assignments / flag values to keep annotating across SPA navigation.
  // Render nothing — no beacon effect, no FlagValues — so the feature adds zero
  // per-page work where it doesn't apply.
  if (!eligible && assignments.length === 0 && flagValues === undefined) {
    return null
  }

  return (
    <ExperimentExposureProvider assignments={assignments}>
      <ExperimentExposureBeacon />
      {flagValues !== undefined ? (
        <FlagValues values={flagValues as never} />
      ) : null}
    </ExperimentExposureProvider>
  )
}
